import {CollapsibleNavList} from '@github-ui/layout-primitives/CollapsibleNavList'
import {useSidebarPageLayoutActions} from '@github-ui/layout-primitives/SidebarPageLayout'
import {getIconComponent, type SidebarLink} from '@github-ui/saved-views'
import {useAnalytics} from '@github-ui/use-analytics'
import {TriangleDownIcon} from '@primer/octicons-react'
import {Button, NavList, Truncate} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useRef, useState} from 'react'

import {getHandlerNavItemClick} from '../utils/sidebar-link'
import styles from './ListSidebar.module.css'
import {SidebarLinkIcon} from './SidebarLinkIcon'

interface ListSidebarProps {
  links: SidebarLink[]
  onSelectLink: (linkItem: SidebarLink) => void
  selectedLink: string
  useCollapsibleLayout?: boolean
}

/**
 * Note: This is split out in order to support links closing the sidebar, and without introducing a hook
 * to the non-enabled (legacy) version of the sidebar. This is a temporary solution until
 * the sidebar is fully migrated to the new layout primitives. Once that is done, this component content can be moved
 * to ListSidebar directly.
 */
const ListSidebarCollapsible = ({links, selectedLink, onSelectLink}: ListSidebarProps) => {
  const {sendAnalyticsEvent} = useAnalytics()
  const {closeDialog} = useSidebarPageLayoutActions()
  return (
    <CollapsibleNavList aria-label="Repository views">
      {links.map(linkItem => (
        <CollapsibleNavList.LinkItem
          key={linkItem.id}
          to={`?q=${linkItem.query}`}
          forceHardNavigation
          icon={getIconComponent(linkItem.icon)}
          label={linkItem.name}
          aria-current={selectedLink === linkItem.id && 'page'}
          onClick={getHandlerNavItemClick({
            onSelectLink: selectedShortcut => {
              closeDialog()
              onSelectLink(selectedShortcut)
            },
            sendAnalyticsEvent,
            linkItem,
          })}
        />
      ))}
    </CollapsibleNavList>
  )
}

export function ListSidebar({links, selectedLink, onSelectLink, useCollapsibleLayout = false}: ListSidebarProps) {
  const {sendAnalyticsEvent} = useAnalytics()

  if (useCollapsibleLayout) {
    return <ListSidebarCollapsible links={links} selectedLink={selectedLink} onSelectLink={onSelectLink} />
  }

  return (
    <NavList>
      {links.map(linkItem => (
        <NavList.Item
          key={linkItem.id}
          aria-current={selectedLink === linkItem.id && 'page'}
          href={`?q=${linkItem.query}`}
          onClick={getHandlerNavItemClick({onSelectLink, sendAnalyticsEvent, linkItem})}
        >
          <NavList.LeadingVisual>
            <SidebarLinkIcon name={linkItem.icon} color={linkItem.color} />
          </NavList.LeadingVisual>
          <Truncate title={linkItem.name} maxWidth="100%">
            {linkItem.name}
          </Truncate>
        </NavList.Item>
      ))}
    </NavList>
  )
}

interface ListSidebarCollapsedButtonProps extends ListSidebarProps {
  onCreateView?: () => void
  showCreateShortcuts?: boolean
}

export function ListSidebarCollapsedButton({
  onSelectLink,
  onCreateView,
  showCreateShortcuts = false,
  ...props
}: ListSidebarCollapsedButtonProps) {
  const [isDialogOpen, showDialog] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const selectedLinkItem = props.links.find(link => link.id === props.selectedLink)
  const listName = selectedLinkItem?.name || 'Search'

  return (
    <>
      <Button
        ref={buttonRef}
        variant="invisible"
        onClick={() => showDialog(true)}
        trailingVisual={TriangleDownIcon}
        aria-describedby="types-collapsed-button-description"
        className={styles.sidebarToggleButton}
      >
        <h1 className={styles.sidebarHeading}>
          <Truncate title={listName} maxWidth="100%">
            {listName}
          </Truncate>
        </h1>
      </Button>
      <span className="sr-only" id="types-collapsed-button-description" aria-live="polite">
        Change the repository type filter
      </span>
      {isDialogOpen && (
        <Dialog
          className={styles.dialog}
          onClose={() => showDialog(false)}
          title="Views"
          width="small"
          returnFocusRef={buttonRef}
          footerButtons={
            showCreateShortcuts && onCreateView
              ? [
                  {buttonType: 'default', content: 'Close', onClick: () => showDialog(false)},
                  {
                    buttonType: 'primary',
                    content: 'New view',
                    onClick: onCreateView,
                  },
                ]
              : []
          }
        >
          <ListSidebar
            onSelectLink={linkItem => {
              onSelectLink(linkItem)
              showDialog(false)
            }}
            {...props}
          />
        </Dialog>
      )}
    </>
  )
}
